import { keys as lodashKeys, get, set, each, isObject, concat, find, reduce, map, some, isEmpty, chain, omit } from "lodash"
import { produce } from "immer"
import { computeRemoveFilter, INTRADAY_PRICING_FIELD } from "./filters"
import { ATTRIBUTION_METHODS } from "./risk"

export const FILTERS = {
  LONG_SHORT: "longShort",
  COUNTRY: "country",
  CURRENCY: "currency",
  ASSET_CLASS: "assetClass",
  ASSET_SUBCLASS: "assetSubclass",
  AVG_DAILY_VOLUME: "averageDailyVolume",
  MARKET_CAPITALIZATION: "marketCapitalization",
}

export const NORMALIZATION = {
  CONTRIBUTION: "CONTRIBUTION",
  STANDALONE: "STANDALONE",
}

export function segmentPositionSet(positionSet, segment) {
  if (!segment) { return positionSet }
  return {
    segment,
    ...positionSet,
  }
}

export function getSegmentLabel(segment, item) {
  if (!segment && get(item, "label")) { return item.label }
  const keys = getAllKeys(segment)
  let label

  const key = keys.find((key) => key.match(/longShort/gi))
  if (key) {
    label = get(segment, key)
  }
  // Add more cases for different naming strategies
  // key = keys.find((key) => key.match(/currency/))

  if (!item) { return label }

  if (get(item, "label", "").indexOf(label) !== -1) {
    return item.label
  }

  return `${item.label} - ${label}`
}

export function getSegmentNormalization(item) {
  return get(item, "positionSet.segment.normalization") || get(item, "segment.normalization") || get(item, "normalization")
}

export function changeSegmentNormalization(item, normalization) {
  // `item` is a live reference to a comparison item in Redux state; the raw set() below would
  // mutate it in place and trip RTK's immutableCheck. Run the same write on an immer draft so a
  // fresh, output-identical item is returned instead.
  return produce(item, draft => {
    set(draft, "segment.normalization", normalization)
  })
}

export function isSegmentUnmodeled(segment, experiment, isRefUnmodeled) {
  const isShort = get(experiment, "config.setup.tradeSize", 0) < 0
  const keys = getAllKeys(segment)
  const longShort = find(keys, (key) => key.match(/longShort/gi))
  if (longShort && get(segment, longShort) === (isShort ? "SHORT" : "LONG") && isRefUnmodeled) {
    return true
  }
  return false
}

function getAllKeys(item, prevKey) {
  let keys = []
  each(lodashKeys(item), (key) => {
    if (isObject(item[key])) {
      keys = concat(keys, getAllKeys(item[key], prevKey ? `${prevKey}.${key}` : key))
    } else {
      keys.push(prevKey ? `${prevKey}.${key}` : key)
    }
  })
  return keys
}

export function computeNonNullSegment(filter, normalization) {
  return { filters: filter ?? [], normalization }
}

export function computeFilteredSegment(filter, normalization) {
  if (!filter) {
    return null
  }

  let updatedFilter = filter

  // Convert securities to segment filter format
  if (filter.securities) {
    updatedFilter = {
      ...filter,
      securities: reduce(filter.securities, (securitiesFilter, securityIds, operator) => {
        securitiesFilter[operator] = map(securityIds, securityId => ({
          id: getFilterSecurityId(securityId),
        }))
        return securitiesFilter
      }, {}),
    }
  }

  return {
    filters: updatedFilter,
    normalization,
  }
}

function getFilterSecurityId(security) {
  if (get(security, "assetClass") === "index") {
    return { indexId: security.indexId || security.ticker }
  }

  return chain(security).pick(["ticker", "mic", "country", "internalId"]).omitBy(v => !v).value()
}

// Filter types the base (and, once active, the item itself) can never carry - see removeUnsupportedBaseFilters.
const UNSUPPORTED_ACTIVE_FILTER_TYPES = ["tag", "positionAttributes"]

/**
 * Returns true if the given filter is supported in active mode with normalization set to "Contribution".
 */
export const filterSupportsContribution = (filter, isActive) => {
  if (!filter || !isActive) { return true }
  return !some(UNSUPPORTED_ACTIVE_FILTER_TYPES, filterType => get(filter, filterType))
}

/**
 * Same check as filterSupportsContribution, but for the filterOn-shaped rows a filter picker builds
 * before they're committed to a segment - lets a caller check the picker's live, in-progress state
 * (not yet run through computeUpdatedFilters) instead of only the already-saved segment filter.
 */
export const filterRowsSupportContribution = (filterRows, isActive) => {
  if (!isActive) { return true }
  return !filterRows?.some(row => UNSUPPORTED_ACTIVE_FILTER_TYPES.includes(row.filterOn?.type))
}

function stripFilterTypes(filter, filterTypes) {
  return reduce(filterTypes, (currentFilter, filterType) => {
    if (get(currentFilter, filterType)) {
      return computeRemoveFilter(currentFilter, filterType, [])
    }
    return currentFilter
  }, filter)
}

export const removeUnsupportedBaseFilters = (filter) => {
  if (!filter) {
    return filter
  }

  return stripFilterTypes(filter, UNSUPPORTED_ACTIVE_FILTER_TYPES)
}

/**
 * Removes filters that are not supported, based on active mode and normalization. Does not mutate the original filter.
 */
export const removeUnsupportedFilters = (filter, isActive, normalization) => {
  if (!isActive || normalization === NORMALIZATION.STANDALONE) {
    return filter
  }

  return removeUnsupportedBaseFilters(filter)
}

export const removeUnsupportedAddedFilters = (addedFilters, isActive, normalization) => {
  if (!addedFilters || !isActive || normalization === NORMALIZATION.STANDALONE) {
    return addedFilters
  }

  return addedFilters.filter(filter => !UNSUPPORTED_ACTIVE_FILTER_TYPES.includes(filter.filterOn.type))
}

export function removeIntradayPricingFilter(filter) {
  if (!filter || filter[INTRADAY_PRICING_FIELD] === undefined) {
    return filter
  }
  const remainingFilter = omit(filter, INTRADAY_PRICING_FIELD)
  return isEmpty(remainingFilter) ? null : remainingFilter
}

// "As of today" intraday pricing has no meaning once the range ends in the past, so the field is
// dropped outside intraday mode. Single primitive shared by every query path (instrument/base
// segments, graphs, drilldown) so none can filter over an inconsistent population.
export function computeModeFilteredSegment(filter, normalization, isIntradayMode) {
  return computeFilteredSegment(isIntradayMode ? filter : removeIntradayPricingFilter(filter), normalization)
}

export function computeMCTEAdjustedSegment(segment, attributionMethod, isActive) {
  if (isEmpty(segment)) { return segment }
  if (isActive && attributionMethod === ATTRIBUTION_METHODS.MCTE) {
    return {
      ...segment,
      normalization: NORMALIZATION.STANDALONE,
    }
  }
  return segment
}

export function computeMCTEAdjustedBase(base, attributionMethod, isActive) {
  if (isActive && attributionMethod === ATTRIBUTION_METHODS.MCTE && get(base, "segment")) {
    return set({ ...base }, "segment.normalization", NORMALIZATION.STANDALONE)
  }
  return base
}
